import { getRequests, getResponseById } from "../services/copilot";
import { useSwrManagement } from "~/hooks/useSwrManagement";
import { RequestResponseViewModel, ResponseViewModel } from "../types/copilot";
import { mutate } from "swr";
import { GET_REQUESTS } from "~/constants/swrKey";
import {
  mapMikeMessageParameters,
  mapRequestToRequestResponseViewModel,
} from "../helpers/copilotMessageMapper";
import { v4 as uuidv4 } from "uuid";
import { useEffect } from "react";
import { useAuthenticationContext } from "~/providers/AuthenticationProvider";
import { getToken } from "~/storages/token";

const fetcher = async (
  token: string,
  conversationId: string,
  dateISO?: string
) => {
  if (!token || !conversationId) return undefined;

  const requests = await getRequests(conversationId, dateISO);
  const data = mapRequestToRequestResponseViewModel(requests, true);

  return data;
};

export const useGetRequests = (
  conversationId: string,
  responseId?: string,
  relatedResponses?: ResponseViewModel[]
) => {
  const { token } = useAuthenticationContext();
  const swrKey = createSWRKey(conversationId);

  const { data, loading } = useSwrManagement({
    swrKey,
    fetcher: () => fetcher(token || "", conversationId),
  });

  useEffect(() => {
    const asyncGetResponseById = async () => {
      if (
        responseId &&
        (data?.responses.some((r) => r.id === responseId) === false ||
          relatedResponses?.length) &&
        !loading
      ) {
        if (
          relatedResponses?.length &&
          relatedResponses.some((r) => r.id === responseId)
        ) {
          reloadRequests(conversationId, {
            requests: [],
            responses: relatedResponses,
          });
        } else {
          const response = await getResponseById(responseId);
          if (response) {
            reloadRequests(conversationId, {
              requests: [],
              responses: [
                {
                  ...response,
                  params: mapMikeMessageParameters(response),
                  requestId: uuidv4(),
                },
              ],
            });
          }
        }
      }
    };
    asyncGetResponseById();
  }, [data, responseId, conversationId, loading, relatedResponses]);

  return {
    requests: data?.requests,
    responses: data?.responses,
    requestLoading: !data || loading,
  };
};

export const reloadRequests = (
  conversationId: string,
  newData: RequestResponseViewModel,
  /**
   * @description If True comes, if the False will make the Operation Date ranking, the data that will work according to Business Rule will be added to the end
   */
  isBefore?: boolean
) => {
  mutate(
    createSWRKey(conversationId),
    (oldData: RequestResponseViewModel | undefined) => {
      return {
        requests: (isBefore ? (newData?.requests ?? []) : [])
          .concat(oldData?.requests ?? [])
          .concat(!isBefore ? (newData?.requests ?? []) : [])
          .filter(
            (item, index, list) =>
              list.findIndex((l) => l.id === item.id) === index
          ),
        responses: (isBefore ? (newData?.responses ?? []) : [])
          .concat(oldData?.responses ?? [])
          .concat(!isBefore ? (newData?.responses ?? []) : [])
          .filter(
            (item, index, list) =>
              list.findIndex((l) => l.id === item.id) === index
          ),
      } as RequestResponseViewModel;
    },
    false
  );
};

export const getPreviousRequests = async (
  conversationId: string,
  dateISO?: string
): Promise<boolean> => {
  const token = getToken();

  if (!token) return false;

  const newData = await fetcher(token, conversationId, dateISO);

  if (newData) {
    reloadRequests(conversationId, newData, true);
    return newData.responses.length > 0;
  }

  return false;
};

const createSWRKey = (conversationId: string) => {
  return [GET_REQUESTS, conversationId].filter(Boolean).join("|");
};
